/**
 * AggregatePositionsCard — rolled-up view of all positions across the
 * broker's agency clients. Institutional-admin dashboards replace the
 * personal trading terminal with this view (item 7 of Feb 2026 spec).
 *
 * Endpoint: GET /api/agency/positions?status=open|closed|all
 * Shows: client name/email, symbol, side, qty, entry, exit (if closed), PnL.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtKES } from "@/lib/api";
import { TrendingUp, TrendingDown, Users, Filter } from "lucide-react";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";

const TABS = [
    { key: "open",   label: "Open",   testId: "agg-tab-open" },
    { key: "closed", label: "Closed", testId: "agg-tab-closed" },
    { key: "all",    label: "All",    testId: "agg-tab-all" },
];

export default function AggregatePositionsCard({ vocab, isMoE }) {
    const [status, setStatus] = useState("open");
    const [data, setData]     = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get(`/agency/positions?status=${status}`);
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load positions");
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

    const positions = data?.positions || [];
    const openPnl   = data?.open_pnl ?? 0;
    const closedPnl = data?.closed_pnl ?? 0;
    const totalPnl  = openPnl + closedPnl;
    const { sorted: sortedPositions, sortKey, direction, requestSort } = useSortableData(positions, {
        accessors: {
            client: (p) => p.client_name || p.client_email || "",
            symbol: (p) => p.symbol || "",
            side:   (p) => p.side || "",
            qty:    (p) => Number(p.qty || 0),
            entry:  (p) => Number(p.entry_price || 0),
            exit:   (p) => Number(p.exit_price ?? -Infinity),
            pnl:    (p) => Number(p.pnl || 0),
            status: (p) => p.status || "",
        },
    });

    return (
        <div className="border border-border bg-card rounded overflow-hidden" data-testid="aggregate-positions-card">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" />
                    <div className="overline">
                        Aggregate positions · {data?.clients_count ?? 0} {vocab?.memberPlural || "clients"}
                    </div>
                </div>
                <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setStatus(t.key)}
                            data-testid={t.testId}
                            className={"px-3 py-1 " + (
                                status === t.key
                                    ? "bg-primary text-primary-foreground font-semibold"
                                    : "hover:bg-secondary/40"
                            )}
                        >{t.label}</button>
                    ))}
                </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-3 gap-0 border-b border-border text-sm">
                <Kpi label="Open PnL"   value={fmtKES(openPnl)}   tone={signTone(openPnl)} testId="agg-kpi-open" />
                <Kpi label="Closed PnL" value={fmtKES(closedPnl)} tone={signTone(closedPnl)} testId="agg-kpi-closed" />
                <Kpi label="Total PnL"  value={fmtKES(totalPnl)}  tone={signTone(totalPnl)} testId="agg-kpi-total" />
            </div>

            {loading ? (
                <div className="p-6 text-center text-xs text-muted-foreground">Loading positions…</div>
            ) : positions.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground" data-testid="agg-empty">
                    No {status !== "all" ? status : ""} positions yet.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="agg-positions-table">
                        <thead className="bg-secondary/30">
                            <tr className="text-left text-muted-foreground uppercase tracking-widest text-[10px]">
                                <SortableTh label={isMoE ? "Student / Staff" : "Client / Trader"} sortKey="client" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3 py-2" />
                                <SortableTh label="Symbol" sortKey="symbol" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3 py-2" />
                                <SortableTh label="Side"   sortKey="side"   currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3 py-2" />
                                <SortableTh label="Qty"    sortKey="qty"    currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3 py-2" />
                                <SortableTh label="Entry"  sortKey="entry"  currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3 py-2" />
                                <SortableTh label="Exit"   sortKey="exit"   currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3 py-2" />
                                <SortableTh label="PnL"    sortKey="pnl"    currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="px-3 py-2" />
                                <SortableTh label="Status" sortKey="status" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {sortedPositions.map((p) => (
                                <tr key={p.id} data-testid={`agg-pos-${p.id}`}>
                                    <td className="px-3 py-2">
                                        <div className="font-semibold">{p.client_name || "—"}</div>
                                        <div className="text-[10px] text-muted-foreground">{p.client_email}</div>
                                    </td>
                                    <td className="px-3 py-2 mono font-bold">{p.symbol}</td>
                                    <td className="px-3 py-2 uppercase">
                                        <span className={p.side === "buy" ? "text-success" : "text-danger"}>
                                            {p.side}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 mono">{p.qty}</td>
                                    <td className="px-3 py-2 mono">{p.entry_price}</td>
                                    <td className="px-3 py-2 mono">{p.exit_price ?? "—"}</td>
                                    <td className={`px-3 py-2 mono text-right ${toneClassName(signTone(p.pnl))}`}>
                                        {signedPrefix(p.pnl)}{fmtKES(p.pnl)}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={"px-2 py-0.5 rounded-full text-[9px] font-bold uppercase " + (
                                            p.status === "closed"
                                                ? "bg-secondary text-muted-foreground"
                                                : "bg-primary/10 text-primary"
                                        )}>
                                            {p.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value, tone, testId }) {
    const cls = toneClassName(tone);
    return (
        <div className="px-4 py-3 border-r border-border last:border-r-0 min-w-0" data-testid={testId}>
            <div className="overline text-muted-foreground truncate">{label}</div>
            <div className={`mono font-bold text-lg whitespace-nowrap ${cls}`}>{value}</div>
        </div>
    );
}
